/**
 * Sound Settings Component
 * Allows users to configure their sound notification preferences
 */
import React from 'react';
import { 
  Volume2, VolumeX, Bell, MessageSquare, UserPlus, UserMinus,
  CheckCircle, AlertTriangle, XCircle, AtSign, Play
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { useSound } from '../../contexts/SoundContext';

const SOUND_TYPE_INFO = {
  message: {
    label: 'New Messages',
    description: 'Play sound for incoming chat messages',
    icon: MessageSquare,
  },
  messagePriority: {
    label: 'High Priority Messages',
    description: 'Play sound for urgent/high-priority messages',
    icon: Bell,
  },
  mention: {
    label: 'Mentions',
    description: 'Play sound when someone mentions you',
    icon: AtSign,
  },
  userLogin: {
    label: 'User Login',
    description: 'Play sound when a team member comes online',
    icon: UserPlus,
  },
  userLogout: {
    label: 'User Logout',
    description: 'Play sound when a team member goes offline',
    icon: UserMinus,
  },
  success: {
    label: 'Success',
    description: 'Play sound for successful actions',
    icon: CheckCircle,
  },
  warning: {
    label: 'Warnings',
    description: 'Play sound for warnings and alerts',
    icon: AlertTriangle,
  },
  error: {
    label: 'Errors',
    description: 'Play sound for error notifications',
    icon: XCircle,
  },
};

export function SoundSettings() {
  const { 
    preferences, 
    toggleSound, 
    toggleSoundType, 
    setVolume, 
    testSound,
    isUnlocked 
  } = useSound();

  return (
    <Card data-testid="sound-settings-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {preferences.enabled ? (
                <Volume2 className="h-5 w-5 text-primary" />
              ) : (
                <VolumeX className="h-5 w-5 text-muted-foreground" />
              )}
              Sound Notifications
            </CardTitle>
            <CardDescription>
              Configure audio alerts for important events
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {!isUnlocked && (
              <Badge variant="outline" className="text-amber-600 border-amber-600/30">
                Click anywhere to enable
              </Badge>
            )}
            <Switch
              checked={preferences.enabled}
              onCheckedChange={toggleSound}
              data-testid="sound-master-toggle"
            />
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Volume Control */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Master Volume</Label>
            <span className="text-sm text-muted-foreground">
              {Math.round(preferences.volume * 100)}%
            </span>
          </div>
          <div className="flex items-center gap-3">
            <VolumeX className="h-4 w-4 text-muted-foreground" />
            <Slider
              value={[preferences.volume * 100]}
              onValueChange={([val]) => setVolume(val / 100)}
              max={100}
              step={5}
              className="flex-1"
              disabled={!preferences.enabled}
              data-testid="sound-volume-slider"
            />
            <Volume2 className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        <Separator />

        {/* Individual Sound Settings */}
        <div className="space-y-4">
          <Label className="text-sm font-medium">Sound Types</Label>
          
          <div className="grid gap-3">
            {Object.entries(SOUND_TYPE_INFO).map(([type, info]) => {
              const Icon = info.icon;
              const isEnabled = preferences.sounds[type];
              
              return (
                <div 
                  key={type}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    isEnabled && preferences.enabled 
                      ? 'bg-accent/50 border-accent' 
                      : 'bg-muted/30 border-transparent'
                  }`}
                  data-testid={`sound-type-${type}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-md ${
                      isEnabled && preferences.enabled 
                        ? 'bg-primary/10 text-primary' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{info.label}</p>
                      <p className="text-xs text-muted-foreground">{info.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => testSound(type)}
                      disabled={!preferences.enabled}
                      title="Test sound"
                      data-testid={`sound-test-${type}`}
                    >
                      <Play className="h-3 w-3" />
                    </Button>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => toggleSoundType(type)}
                      disabled={!preferences.enabled}
                      data-testid={`sound-toggle-${type}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <Separator />
        
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Sounds are generated using Web Audio API - no external files needed
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => testSound('success')}
            disabled={!preferences.enabled}
            data-testid="sound-test-all"
          >
            <Play className="h-3 w-3 mr-1" />
            Test Sound
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default SoundSettings;
